import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityService } from 'core/api/services/lookUp';
import { useExperiences } from 'core/contexts/experienceV2';
import { Field, Form } from 'react-final-form';
import { LookUpResource, LookUpResourceCategory } from '@utriper/core-ui';
import { Box, Button, CircularProgress, FormHelperText, Grid2, InputLabel, Typography } from '@mui/material';

const AddResourceForm = ({ experienceId, handleClose = () => {}, isCategory = false }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`experience_resource.add_form.${key}`);
    const { addResource, submittingRes } = useExperiences();
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async ({ resourceId, resourceCategoryId }) => {
        await addResource({ experienceId, resourceId, resourceCategoryId, isCategory });
        handleClose();
    };

    const validateForm = (values) => {
        const errors = {};
        if (!values?.resourceId && !isCategory) {
            errors.resourceId = t('common_form.required');
        }

        if (!values?.resourceCategoryId && isCategory) {
            errors.resourceCategoryId = t('common_form.required');
        }

        return errors;
    };

    let title = translate('resource.title');
    let description = translate('resource.description');

    if (isCategory) {
        title = translate('category.title');
        description = translate('category.description');
    }

    return (
        <Form
            key={`add-resource-form-${experienceId ?? 0}`}
            onSubmit={handleSubmit}
            validate={validateForm}
            render={({ values, handleSubmit, form, errors }) => (
                <form key={`add-resource-form-${experienceId ?? 0}`} onSubmit={handleSubmit} noValidate>
                    <Box component="section" pt={2}>
                        {/* Title */}
                        <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                            <Typography fontSize={20} fontWeight={900} sx={{ color: '#131313' }} noWrap>
                                {title}
                            </Typography>
                            <Typography fontSize={16} fontWeight={500} sx={{ color: '#131313bf' }}>
                                {description}
                            </Typography>
                        </Grid2>

                        {/* Form */}
                        <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                            {isCategory ? (
                                <Grid2
                                    container
                                    width="100%"
                                    direction="column"
                                    justifyContent="flex-start"
                                    alignItems="flex-start"
                                    gap="4px"
                                    sx={{
                                        '& .MuiAutocomplete-root': { width: '100%' },
                                        '& .MuiOutlinedInput-notchedOutline': submitted && errors?.resourceCategoryId ? { borderColor: 'red' } : '',
                                    }}
                                >
                                    <InputLabel
                                        sx={{
                                            fontSize: '18px',
                                            fontWeight: 400,
                                            color: '#131313',
                                        }}
                                    >
                                        {translate('category.label')}
                                    </InputLabel>
                                    <Field
                                        name="resourceCategoryId"
                                        render={({ input }) => (
                                            <LookUpResourceCategory
                                                {...input}
                                                label={translate('category.search')}
                                                entityService={EntityService}
                                                value={values?.resourceCategoryId}
                                                initialValue={values?.resourceCategoryId}
                                                onChange={(newValue) => {
                                                    form.change('resourceCategoryId', newValue);
                                                }}
                                            />
                                        )}
                                    />
                                    {submitted && errors?.resourceCategoryId && <FormHelperText error={submitted && errors?.resourceCategoryId}>{errors.resourceCategoryId}</FormHelperText>}
                                </Grid2>
                            ) : (
                                <Grid2
                                    container
                                    width="100%"
                                    direction="column"
                                    justifyContent="flex-start"
                                    alignItems="flex-start"
                                    gap="4px"
                                    sx={{
                                        '& .MuiAutocomplete-root': { width: '100%' },
                                        '& .MuiOutlinedInput-notchedOutline': submitted && errors?.resourceId ? { borderColor: 'red' } : '',
                                    }}
                                >
                                    <InputLabel
                                        sx={{
                                            fontSize: '18px',
                                            fontWeight: 400,
                                            color: '#131313',
                                        }}
                                    >
                                        {translate('resource.label')}
                                    </InputLabel>
                                    <Field
                                        name="resourceId"
                                        render={({ input }) => (
                                            <LookUpResource
                                                {...input}
                                                label={translate('resource.search')}
                                                entityService={EntityService}
                                                value={values?.resourceId}
                                                initialValue={values?.resourceId}
                                                onChange={(newValue) => {
                                                    form.change('resourceId', newValue);
                                                }}
                                            />
                                        )}
                                    />
                                    {submitted && errors?.resourceId && <FormHelperText error={submitted && errors?.resourceId}>{errors.resourceId}</FormHelperText>}
                                </Grid2>
                            )}
                        </Grid2>

                        {/* Action Buttons */}
                        <Grid2 container justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems="center" gap="12px" mt="16px">
                            <Button
                                sx={{
                                    padding: '8px 14px',
                                    borderColor: '#13131340',
                                    fontWeight: 700,
                                    width: { xs: '100%', md: 'fit-content' },
                                    textTransform: 'none',
                                }}
                                variant="outlined"
                                disabled={submittingRes}
                                onClick={(e) => {
                                    handleClose();
                                    return form.reset(e);
                                }}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                sx={{
                                    padding: '8px 14px',
                                    backgroundColor: 'var(--color-utriper-blue)',
                                    fontWeight: 700,
                                    width: { xs: '100%', md: 'fit-content' },
                                    textTransform: 'none',
                                }}
                                loading={submittingRes}
                                loadingIndicator={
                                    <Box sx={{ display: 'flex' }}>
                                        <CircularProgress size={20} sx={{ color: '#131313bf' }} />
                                    </Box>
                                }
                                variant="contained"
                                type="submit"
                                onClick={(e) => {
                                    setSubmitted(true);
                                    return handleSubmit(e);
                                }}
                            >
                                {t('common.add')}
                            </Button>
                        </Grid2>
                    </Box>
                </form>
            )}
        />
    );
};

export default AddResourceForm;
